import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { reservationLinks } from "@/components/lib/SidebarLinks";
import { Button } from "@/components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "@/hooks/useAuth";
import StaffTableSkeleton from "@/components/skeleton/Staff-skeletion";
import { CardContent, Card } from "@/components/ui/card";
import { fetchReservationById } from "@/redux/slice/reservation-slice/reservation-slice";
import { useParams, Link } from "react-router-dom";
import { 
  editReservation, 
  markDepositPaidThunk, 
  sendDepositLinkThunk, 
  cancelDepositLinkThunk,
  rejectAndRefundDepositThunk, 
  terminalPaymentReservationThunk 
} from "@/redux/slice/reservation-slice/reservation-slice";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AssignTableModal from "@/components/reservation/AssignTableModal";
import CancelDialog from "@/components/reservation/CancelDialog";
import SendPaymentLinkDialog from "@/components/reservation/SendPaymentLinkDialog";
import CancelDepositPaymentLinkDialog from "@/components/reservation/CancelDepositPaymentLinkDialog";
import PendingPayLinkConflictDialog from "@/components/common/PendingPayLinkConflictDialog";
import TerminalPaymentResponseDialog from "@/components/reservation/TerminalPaymentResponseDialog";
import ReservationRefundResponseDialog from "@/components/reservation/ReservationRefundResponseDialog";
import PaxPaymentTimeoutDialog from "@/components/TakeOder/PaxPaymentTimeoutDialog";
import hasPermission from "@/hooks/hasPermission";
import formatTo12HourTime from "@/lib/formatTo12HourTime";
import useSettings from "@/hooks/useSettings";
import PusherHandler from "@/api/lib/pusher/PusherHandler";
import { 
  CreditCard, 
  Banknote, 
  Link2, 
  Link2Off,
  User, 
  Phone, 
  Mail, 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle2, 
  AlertCircle, 
  XCircle, 
  Utensils, 
  ShieldCheck, 
  Award, 
  FileText, 
  Radio,
  Layers,
  Copy,
  Check,
  Building2,
  Info,
  CalendarCheck,
  Hash,
  RotateCcw,
  LayoutGrid,
  AlertTriangle,
  DollarSign
} from "lucide-react";

function ReservationDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Cancel dialog (single dialog, title/description changes based on deposit state)
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [payByLinkDialog, setPayByLinkDialog] = useState(false);
  const [cancelDepositLinkDialog, setCancelDepositLinkDialog] = useState(false);
  const [cancelDepositLinkLoading, setCancelDepositLinkLoading] = useState(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [conflictTargetChannel, setConflictTargetChannel] = useState("email");
  const [isConflictLoading, setIsConflictLoading] = useState(false);
  const [payByPhoneDialog, setPayByPhoneDialog] = useState(false);
  const [payByPhoneLoading, setPayByPhoneLoading] = useState(false);
  const [payByLinkLoading, setPayByLinkLoading] = useState(false);
  const [cardPaymentLoading, setCardPaymentLoading] = useState(false);
  const [cardPaymentDialogOpen, setCardPaymentDialogOpen] = useState(false);
  const [cardPaymentResponse, setCardPaymentResponse] = useState(null);
  const [refundResponseDialogOpen, setRefundResponseDialogOpen] = useState(false);
  const [refundResponseData, setRefundResponseData] = useState(null);
  const [isPaxTimeoutDialogOpen, setIsPaxTimeoutDialogOpen] = useState(false);
  const [paxTimeoutData, setPaxTimeoutData] = useState(null);
  const [isRefundAction, setIsRefundAction] = useState(false);
  const [isCancelledEvent, setIsCancelledEvent] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const { user } = useAuth();
  const { id } = useParams();
  const { singleReservation, singleLoading } = useSelector(
    (state) => state.reservation.reservation,
  );
  const { settings } = useSettings();
  const { addEventHandler, removeEventHandler } = PusherHandler();

  useEffect(() => {
    dispatch(fetchReservationById(id));
  }, [id, dispatch]);

  useEffect(() => {
    const handleReservationUpdate = (data) => {
      if (data?.reservation_id?.toString() === id?.toString() || data?.id?.toString() === id?.toString()) {
        // 1. Reservation-level Cancellation / Rejection
        if (
          data?.event_type === "reservation_rejected" ||
          data?.event_type === "reservation_cancelled" ||
          data?.type === "reservation_rejected" ||
          data?.type === "reservation_cancelled" ||
          data?.reservation_status === "cancelled" ||
          data?.reservation_status === "rejected" ||
          data?.reservation?.status === "cancelled" ||
          data?.reservation?.status === "rejected"
        ) {
          setIsCancelledEvent(true);
          setCardPaymentDialogOpen(false);
          setCardPaymentResponse(null);
          setRefundResponseDialogOpen(false);
          setRefundResponseData(null);
          setIsPaxTimeoutDialogOpen(false);
          setPaxTimeoutData(null);
          setCardPaymentLoading(false);
        } else if (
          data?.reservation_status ||
          data?.reservation?.status ||
          data?.event_type === "reservation_confirmed" ||
          data?.event_type === "reservation_accepted" ||
          data?.type === "reservation_confirmed" ||
          data?.type === "reservation_accepted"
        ) {
          setIsCancelledEvent(false);
        }

        // 2. Deposit / Payment Cancelled or Failed -> Close all deposit & PAX popups immediately
        if (
          data?.event_type === "reservation_deposit_cancelled" ||
          data?.event_type === "reservation_deposit_payment_cancelled" ||
          data?.event_type === "reservation_payment_cancelled" ||
          data?.event_type === "terminal_payment_cancelled" ||
          data?.event_type === "reservation_refund_cancelled" ||
          data?.event_type === "reservation_deposit_payment_failed" ||
          data?.event_type === "reservation_payment_failed" ||
          data?.type === "reservation_deposit_cancelled" ||
          data?.type === "reservation_deposit_payment_cancelled" ||
          data?.type === "reservation_payment_cancelled" ||
          data?.type === "terminal_payment_cancelled" ||
          data?.type === "reservation_refund_cancelled" ||
          data?.type === "reservation_deposit_payment_failed" ||
          data?.type === "reservation_payment_failed"
        ) {
          setCancelDepositLinkDialog(false);
          setPayByLinkDialog(false);
          setIsConflictDialogOpen(false);
          setConflictData(null);
          setPayByPhoneDialog(false);
          setCardPaymentDialogOpen(false);
          setCardPaymentResponse(null);
          setRefundResponseDialogOpen(false);
          setRefundResponseData(null);
          setIsPaxTimeoutDialogOpen(false);
          setPaxTimeoutData(null);
          setCardPaymentLoading(false);
          setPayByLinkLoading(false);
          setCancelDepositLinkLoading(false);
          setPayByPhoneLoading(false);
          setIsCancelledEvent(false);
        }

        // 3. Deposit / Payment Paid or Refunded successfully -> Close all deposit & payment popups
        if (
          data?.event_type === "reservation_deposit_paid" ||
          data?.event_type === "reservation_deposit_refunded" ||
          data?.event_type === "terminal_payment_approved" ||
          data?.type === "reservation_deposit_paid" ||
          data?.type === "reservation_deposit_refunded" ||
          data?.type === "terminal_payment_approved" ||
          data?.deposit_status === "paid" ||
          data?.advance_paid
        ) {
          setCancelDepositLinkDialog(false);
          setPayByLinkDialog(false);
          setIsConflictDialogOpen(false);
          setConflictData(null);
          setPayByPhoneDialog(false);
          setCancelDialog(false);
          setCardPaymentDialogOpen(false);
          setCardPaymentResponse(null);
          setRefundResponseDialogOpen(false);
          setRefundResponseData(null);
          setIsPaxTimeoutDialogOpen(false);
          setPaxTimeoutData(null);
          setCardPaymentLoading(false);
          setPayByLinkLoading(false);
          setCancelDepositLinkLoading(false);
          setPayByPhoneLoading(false);

          if (data?.event_type === "reservation_deposit_paid" || data?.type === "reservation_deposit_paid") {
            toast.success("Deposit payment received successfully!");
          } else if (data?.event_type === "reservation_deposit_refunded" || data?.type === "reservation_deposit_refunded") {
            toast.success("Deposit refund completed successfully!");
          }
        }

        // 4. Payment Timeout
        if (
          data?.event_type === "reservation_deposit_timeout" ||
          data?.event_type === "reservation_refund_timeout" ||
          data?.type === "reservation_deposit_timeout" ||
          data?.type === "reservation_refund_timeout" ||
          data?.type === "order_payment_timeout" ||
          data?.type === "reservation_payment_timeout" ||
          data?.type === "terminal_payment_timeout" ||
          data?.status === "timeout"
        ) {
          const isRefundTimeout =
            refundResponseDialogOpen ||
            isRefundAction ||
            data?.event_type?.includes("refund") ||
            data?.type?.includes("refund");

          if (isRefundTimeout) {
            setIsRefundAction(true);
          }

          setCardPaymentDialogOpen(false);
          setRefundResponseDialogOpen(false);
          setPaxTimeoutData(data);
          setIsPaxTimeoutDialogOpen(true);
          toast.error(isRefundTimeout ? "PAX terminal refund timed out." : "PAX terminal payment timed out.");
        }

        dispatch(fetchReservationById(id));
      }
    };

    const unsubLive = addEventHandler('live-reservation-update', handleReservationUpdate);
    const unsubDepositPaid = addEventHandler('reservation_deposit_paid', handleReservationUpdate);
    const unsubOrder = addEventHandler('order-updated', handleReservationUpdate);

    return () => {
      if (typeof unsubLive === "function") unsubLive();
      else removeEventHandler('live-reservation-update', handleReservationUpdate);

      if (typeof unsubDepositPaid === "function") unsubDepositPaid();
      else removeEventHandler('reservation_deposit_paid', handleReservationUpdate);

      if (typeof unsubOrder === "function") unsubOrder();
      else removeEventHandler('order-updated', handleReservationUpdate);
    };
  }, [id, addEventHandler, removeEventHandler, dispatch, isRefundAction, refundResponseDialogOpen]);

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const cancelHandler = async () => {
    setCancelLoading(true);
    setIsRefundAction(true);
    const res = await dispatch(rejectAndRefundDepositThunk(singleReservation?.id));
    setCancelLoading(false);
    if (rejectAndRefundDepositThunk.fulfilled.match(res)) {
      setCancelDialog(false);
      const payloadData = res.payload?.data || res.payload;
      if (payloadData?.terminal_payment) {
        setRefundResponseData(res.payload);
        setRefundResponseDialogOpen(true);
      } else {
        toast.success(res.payload?.message || "Reservation cancelled successfully.");
      }
      dispatch(fetchReservationById(id));
    } else if (rejectAndRefundDepositThunk.rejected.match(res)) {
      toast.error(res.payload);
    }
  };

  const handleCancelClick = () => {
    setCancelDialog(true);
  };

  const payByPhoneHandler = async () => {
    setPayByPhoneLoading(true);
    const res = await dispatch(markDepositPaidThunk(singleReservation?.id));
    setPayByPhoneLoading(false);
    if (markDepositPaidThunk.fulfilled.match(res)) {
      toast.success(res.payload?.message || "Deposit marked as paid successfully");
      setPayByPhoneDialog(false);
    } else if (markDepositPaidThunk.rejected.match(res)) {
      toast.error(res.payload);
    }
  };

  const payByLinkHandler = async (channel) => {
    setPayByLinkLoading(true);
    const res = await dispatch(sendDepositLinkThunk({
      reservation_id: singleReservation?.id,
      channel,
    }));
    setPayByLinkLoading(false);
    if (sendDepositLinkThunk.fulfilled.match(res)) {
      toast.success(res.payload?.message || "Deposit payment link sent successfully.");
      setPayByLinkDialog(false);
      setIsPayByLinkDispatched(true);
      // Immediately present the popup dialog to manage / cancel the active pay by link
      setCancelDepositLinkDialog(true);
      dispatch(fetchReservationById(id));
    } else if (sendDepositLinkThunk.rejected.match(res)) {
      const payload = res.payload;
      if (payload?.data?.error_code === "pay_link_already_pending") {
        setConflictData(payload.data.existing_pay_link);
        setConflictTargetChannel(channel);
        setPayByLinkDialog(false);
        setIsConflictDialogOpen(true);
      } else {
        toast.error(payload?.message || (typeof payload === "string" ? payload : "Failed to send deposit link"));
      }
    }
  };

  const handleConflictCancelAndResend = async () => {
    if (!singleReservation?.id) return;
    setIsConflictLoading(true);
    try {
      // 1. Cancel previous link
      const cancelRes = await dispatch(cancelDepositLinkThunk(singleReservation.id));
      if (cancelDepositLinkThunk.fulfilled.match(cancelRes) && cancelRes.payload?.status === "success") {
        toast.success("Previous deposit payment link cancelled. Sending new link...");
        // 2. Resend new link
        const resendRes = await dispatch(sendDepositLinkThunk({
          reservation_id: singleReservation.id,
          channel: conflictTargetChannel
        }));
        if (sendDepositLinkThunk.fulfilled.match(resendRes)) {
          toast.success(`New deposit link sent via ${conflictTargetChannel.toUpperCase()}`);
          setIsConflictDialogOpen(false);
          setConflictData(null);
          setIsPayByLinkDispatched(true);
          setCancelDepositLinkDialog(true);
          dispatch(fetchReservationById(id));
        } else {
          toast.error(resendRes.payload?.message || "Failed to send new deposit link");
        }
      } else {
        toast.error(cancelRes.payload?.message || "Failed to cancel previous deposit link");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setIsConflictLoading(false);
    }
  };

  const handleConflictCancelOnly = async () => {
    if (!singleReservation?.id) return;
    setIsConflictLoading(true);
    try {
      const cancelRes = await dispatch(cancelDepositLinkThunk(singleReservation.id));
      if (cancelDepositLinkThunk.fulfilled.match(cancelRes) && cancelRes.payload?.status === "success") {
        toast.success(cancelRes.payload?.message || "Previous deposit link cancelled.");
        setIsConflictDialogOpen(false);
        setConflictData(null);
        setIsPayByLinkDispatched(false);
        dispatch(fetchReservationById(id));
      } else {
        toast.error(cancelRes.payload?.message || "Failed to cancel previous deposit link");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setIsConflictLoading(false);
    }
  };

  const cancelDepositPaymentLinkHandler = async () => {
    setCancelDepositLinkLoading(true);
    const res = await dispatch(cancelDepositLinkThunk(singleReservation?.id));
    setCancelDepositLinkLoading(false);
    if (cancelDepositLinkThunk.fulfilled.match(res)) {
      if (res.payload?.status === "success") {
        toast.success(res.payload?.message || "Deposit payment link cancelled.");
        setCancelDepositLinkDialog(false);
        setIsPayByLinkDispatched(false);
        dispatch(fetchReservationById(id));
      } else {
        toast.error(res.payload?.message || "Failed to cancel deposit payment link.");
      }
    } else if (cancelDepositLinkThunk.rejected.match(res)) {
      toast.error(res.payload);
    }
  };

  const cardPaymentHandler = async () => {
    setCardPaymentLoading(true);
    setIsRefundAction(false);
    const res = await dispatch(terminalPaymentReservationThunk(singleReservation?.id));
    setCardPaymentLoading(false);
    if (terminalPaymentReservationThunk.fulfilled.match(res)) {
      setCardPaymentResponse(res.payload);
      setCardPaymentDialogOpen(true);
      toast.success(res.payload?.message || "Payment request sent to terminal.");
      dispatch(fetchReservationById(id));
    } else if (terminalPaymentReservationThunk.rejected.match(res)) {
      toast.error(res.payload || "Failed to initiate terminal payment.");
    }
  };

  const [isPayByLinkDispatched, setIsPayByLinkDispatched] = useState(false);

  // is_deposit_paid can be true/false or 1/0
  const isDepositPaid = singleReservation?.is_deposit_paid == true || singleReservation?.is_deposit_paid === 1;
  const isOnline = singleReservation?.reservation_type === "online";
  const isDisabled = isCancelledEvent || singleReservation?.status === "cancelled" || singleReservation?.status === "rejected";
  const depositInfo = singleReservation?.deposit_payment_info;

  const depositStatus = depositInfo?.status?.toLowerCase() || (isDepositPaid ? "paid" : "unpaid");
  const isRefunded = depositStatus === "refunded";
  const isPaid = depositStatus === "paid" || (isDepositPaid && !isRefunded);

  // Check if pay by link is active (must be dispatched/pending and NOT cancelled, paid, or refunded)
  const isLinkActive =
    !isDepositPaid &&
    !isDisabled &&
    depositStatus !== "cancelled" &&
    depositStatus !== "refunded" &&
    depositStatus !== "paid" &&
    (isPayByLinkDispatched ||
      depositInfo?.status?.toLowerCase() === "dispatched" ||
      depositInfo?.status?.toLowerCase() === "pending");

  const [isAssignTableModalOpen, setIsAssignTableModalOpen] = useState(false);
  const closeAssignTableModal = () => {
    setIsAssignTableModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return "N/A";
    }
  };

  if (singleLoading) return <StaffTableSkeleton links={reservationLinks} />;

  const status = singleReservation?.status?.toLowerCase();
  const paymentMethod = depositInfo?.payment_method?.toLowerCase();

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={reservationLinks} />}
      pageName={{
        icon: true,
        title: `Reservation Details`,
      }}
      paths={[
        { name: "Home", path: "/home" },
        {
          name: "Reservation List",
          path: "/reservation",
        },
        {
          name: singleReservation?.unique_id || `ID: ${singleReservation?.id || ""}`,
        },
      ]}
    >
      <div className="container flex flex-col gap-6 mt-4 pb-12">
        
        {/* Top Header Banner Card */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-cblack3 p-5 rounded-xl border border-gray-800 shadow-md">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-cyellow/10 border border-cyellow/20 flex items-center justify-center text-cyellow">
              <CalendarCheck className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-bold text-white tracking-wide">
                  {singleReservation?.unique_id || `Reservation #${singleReservation?.id}`}
                </h2>
                <span className="text-xs px-2 py-0.5 rounded bg-cblack7 text-cwhite3 font-mono">
                  ID: {singleReservation?.id}
                </span>
              </div>
              <p className="text-xs text-cwhite3 mt-1 flex items-center gap-2">
                <span>Created: {formatDate(singleReservation?.created_at || singleReservation?.issue_date)}</span>
                <span>•</span>
                <span className="capitalize">{singleReservation?.type || "Standard Reservation"}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Reservation Type Badge */}
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cblue/15 text-cblue border border-cblue/30 capitalize flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              {singleReservation?.reservation_type === "phone"
                ? "Phone Order"
                : singleReservation?.reservation_type === "in_person"
                ? "In Person"
                : singleReservation?.reservation_type === "online"
                ? "Online Booking"
                : singleReservation?.reservation_type || "Standard"}
            </span>

            {/* Reservation Status Badge */}
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold border capitalize flex items-center gap-1.5 ${
                status === "confirmed"
                  ? "bg-cgreen/15 text-cgreen border-cgreen/30"
                  : status === "cancelled" || status === "rejected"
                  ? "bg-cred/15 text-cred border-cred/30"
                  : "bg-cyellow/15 text-cyellow border-cyellow/30"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  status === "confirmed"
                    ? "bg-cgreen animate-pulse"
                    : status === "cancelled" || status === "rejected"
                    ? "bg-cred"
                    : "bg-cyellow animate-pulse"
                }`}
              />
              {singleReservation?.status || "Pending"}
            </span>
          </div>
        </div>

        {/* 4 Quick Stat Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Party Size */}
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-800/80 flex items-center gap-3.5 shadow-sm">
            <div className="h-10 w-10 rounded-lg bg-sky-500/10 text-sky-400 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-cwhite3 font-medium">Guests</p>
              <p className="text-lg font-bold text-white mt-0.5">
                {singleReservation?.guest || 1} {Number(singleReservation?.guest) > 1 ? "Guests" : "Guest"}
              </p>
            </div>
          </div>

          {/* Reservation Date */}
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-800/80 flex items-center gap-3.5 shadow-sm">
            <div className="h-10 w-10 rounded-lg bg-cyellow/10 text-cyellow flex items-center justify-center shrink-0">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-cwhite3 font-medium">Booking Date</p>
              <p className="text-lg font-bold text-white mt-0.5">
                {formatDate(singleReservation?.reservation_date)}
              </p>
            </div>
          </div>

          {/* Schedule Time Slot */}
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-800/80 flex items-center gap-3.5 shadow-sm">
            <div className="h-10 w-10 rounded-lg bg-cpurple/10 text-cpurple flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-cwhite3 font-medium">Schedule Time</p>
              <p className="text-sm font-bold text-white mt-0.5 truncate">
                {formatTo12HourTime(singleReservation?.start_time)} – {formatTo12HourTime(singleReservation?.end_time)}
              </p>
            </div>
          </div>

          {/* Advance Deposit */}
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-800/80 flex items-center gap-3.5 shadow-sm">
            <div
              className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                isRefunded
                  ? "bg-cyellow/10 text-cyellow"
                  : isPaid
                  ? "bg-cgreen/10 text-cgreen"
                  : "bg-cred/10 text-cred"
              }`}
            >
              {isRefunded ? (
                <RotateCcw className="h-5 w-5" />
              ) : (
                <CreditCard className="h-5 w-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <p className="text-xs text-cwhite3 font-medium">Advance Deposit</p>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase flex items-center gap-1 ${
                    isRefunded
                      ? "bg-cyellow/20 text-cyellow"
                      : isPaid
                      ? "bg-cgreen/20 text-cgreen"
                      : "bg-cred/20 text-cred"
                  }`}
                >
                  {isRefunded ? "Refunded" : isPaid ? "Paid" : "Unpaid"}
                </span>
              </div>
              <p className="text-lg font-bold text-white mt-0.5">
                £{singleReservation?.advance || "0"}
              </p>
            </div>
          </div>
        </div>

        {/* Main Grid: 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Customer Information Card */}
          <Card className="bg-cblack3 border-gray-800 text-white overflow-hidden shadow-sm">
            <div className="p-4 border-b border-gray-800/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-cyellow" />
                <h3 className="font-semibold text-white text-base">Customer Details</h3>
              </div>
              {singleReservation?.customer?.unique_id && (
                <span className="text-xs text-cwhite3 bg-cblack4 px-2 py-0.5 rounded border border-gray-800 font-mono">
                  {singleReservation?.customer?.unique_id}
                </span>
              )}
            </div>
            <CardContent className="p-5 space-y-4">
              {/* Profile Bar */}
              <div className="flex items-center gap-3.5 pb-4 border-b border-gray-800/60">
                <div className="h-12 w-12 rounded-full bg-gradient-to-tr from-cyellow/20 to-cyellow/40 border border-cyellow/30 flex items-center justify-center text-cyellow font-bold text-lg">
                  {singleReservation?.customer?.full_name?.charAt(0)?.toUpperCase() || "C"}
                </div>
                <div>
                  <h4 className="font-bold text-white text-base">
                    {singleReservation?.customer?.full_name || "Guest Customer"}
                  </h4>
                  <p className="text-xs text-cwhite3 mt-0.5 flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-cgreen" />
                    {singleReservation?.customer?.is_banned ? (
                      <span className="text-cred font-semibold">Account Flagged / Banned</span>
                    ) : (
                      <span className="text-cgreen font-medium">Verified Active Customer</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {/* Phone */}
                <div className="bg-cblack4 p-3 rounded-lg border border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Phone className="h-4 w-4 text-cblue shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[11px] text-cwhite3">Phone Number</p>
                      <p className="text-sm font-semibold text-white truncate">
                        {singleReservation?.customer?.number || "N/A"}
                      </p>
                    </div>
                  </div>
                  {singleReservation?.customer?.number && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(singleReservation.customer.number, "phone")}
                      className="p-1.5 text-cwhite3 hover:text-white rounded hover:bg-cblack7 transition shrink-0"
                      title="Copy Phone"
                    >
                      {copiedField === "phone" ? <Check className="h-3.5 w-3.5 text-cgreen" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>

                {/* Email */}
                <div className="bg-cblack4 p-3 rounded-lg border border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Mail className="h-4 w-4 text-cyellow shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[11px] text-cwhite3">Email Address</p>
                      <p className="text-sm font-semibold text-white truncate">
                        {singleReservation?.customer?.email || "N/A"}
                      </p>
                    </div>
                  </div>
                  {singleReservation?.customer?.email && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(singleReservation.customer.email, "email")}
                      className="p-1.5 text-cwhite3 hover:text-white rounded hover:bg-cblack7 transition shrink-0"
                      title="Copy Email"
                    >
                      {copiedField === "email" ? <Check className="h-3.5 w-3.5 text-cgreen" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Loyalty & Rewards Badge */}
              <div className="bg-gradient-to-r from-cyellow/10 to-transparent p-3 rounded-lg border border-cyellow/20 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Award className="h-5 w-5 text-cyellow" />
                  <div>
                    <p className="text-xs font-semibold text-cyellow">Loyalty Rewards</p>
                    <p className="text-[11px] text-cwhite3">
                      Customer Points: {singleReservation?.customer?.points || 0} pts
                    </p>
                  </div>
                </div>
                <span className="text-xs font-bold text-white bg-cblack3 px-2.5 py-1 rounded border border-gray-800">
                  Valued: £{singleReservation?.customer?.points_value || "0.00"}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Deposit & Payment Information Card */}
          <Card className="bg-cblack3 border-gray-800 text-white overflow-hidden shadow-sm">
            <div className="p-4 border-b border-gray-800/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-cgreen" />
                <h3 className="font-semibold text-white text-base">Deposit & Payment Details</h3>
              </div>
              <span
                className={`text-xs px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1.5 ${
                  isRefunded
                    ? "bg-cyellow/20 text-cyellow border border-cyellow/30"
                    : isPaid
                    ? "bg-cgreen/20 text-cgreen border border-cgreen/30"
                    : "bg-cred/20 text-cred border border-cred/30"
                }`}
              >
                {isRefunded ? (
                  <RotateCcw className="h-3.5 w-3.5 animate-spin-slow" />
                ) : isPaid ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5" />
                )}
                {isRefunded ? "Deposit Refunded" : isPaid ? "Deposit Paid" : "Payment Pending"}
              </span>
            </div>

            <CardContent className="p-5 space-y-4">
              {/* Payment Method Banner */}
              <div className="bg-cblack4 p-4 rounded-xl border border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`h-11 w-11 rounded-lg flex items-center justify-center ${
                      isRefunded
                        ? "bg-cyellow/15 text-cyellow"
                        : paymentMethod === "card"
                        ? "bg-cgreen/15 text-cgreen"
                        : paymentMethod === "cash"
                        ? "bg-cviolet/15 text-cviolet"
                        : paymentMethod === "pay_by_link"
                        ? "bg-cblue/15 text-cblue"
                        : "bg-gray-800 text-cwhite3"
                    }`}
                  >
                    {isRefunded ? (
                      <RotateCcw className="h-6 w-6" />
                    ) : paymentMethod === "card" ? (
                      <CreditCard className="h-6 w-6" />
                    ) : paymentMethod === "cash" ? (
                      <Banknote className="h-6 w-6" />
                    ) : paymentMethod === "pay_by_link" ? (
                      <Link2 className="h-6 w-6" />
                    ) : (
                      <CreditCard className="h-6 w-6" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-white text-base capitalize">
                        {isRefunded
                          ? "Refunded to Customer"
                          : paymentMethod === "card"
                          ? "Card Payment"
                          : paymentMethod === "cash"
                          ? "Cash Payment"
                          : paymentMethod === "pay_by_link"
                          ? "Pay by Link (Online)"
                          : depositInfo?.payment_method || (isPaid ? "Manual / Paid" : "Not Paid Yet")}
                      </h4>
                      {depositInfo?.via_terminal && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-cgreen/20 text-cgreen border border-cgreen/30 font-semibold flex items-center gap-1">
                          <Radio className="h-2.5 w-2.5 animate-pulse" />
                          Via Terminal
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-cwhite3 mt-0.5">
                      {isRefunded
                        ? `Advance deposit of £${singleReservation?.advance || "0"} has been refunded to customer`
                        : paymentMethod === "card"
                        ? depositInfo?.via_terminal
                          ? "Processed via Card Terminal Device"
                          : "Direct Card Transaction"
                        : paymentMethod === "cash"
                        ? "Cash collected manually at counter / over phone"
                        : paymentMethod === "pay_by_link"
                        ? "Customer completed payment via direct link"
                        : "No advance payment processed yet"}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-xs text-cwhite3">
                    {isRefunded ? "Refunded Amount" : "Advance Amount"}
                  </span>
                  <p className={`text-xl font-extrabold ${isRefunded ? "text-cyellow" : "text-white"}`}>
                    £{singleReservation?.advance || "0"}
                  </p>
                </div>
              </div>

              {/* Payment Breakdown Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Status */}
                <div className="bg-cblack4/60 p-3 rounded-lg border border-gray-800/80">
                  <span className="text-[11px] text-cwhite3 font-medium">Payment Status</span>
                  <p className={`text-xs font-semibold capitalize mt-1 ${isRefunded ? "text-cyellow" : isPaid ? "text-cgreen" : "text-cred"}`}>
                    {depositInfo?.status || depositStatus}
                  </p>
                </div>

                {/* Payment Method */}
                {paymentMethod && (
                  <div className="bg-cblack4/60 p-3 rounded-lg border border-gray-800/80">
                    <span className="text-[11px] text-cwhite3 font-medium">Payment Method</span>
                    <p className="text-xs font-semibold text-white mt-1">
                      {paymentMethod === "card"
                        ? "Card"
                        : paymentMethod === "cash"
                        ? "Cash"
                        : paymentMethod === "pay_by_link"
                        ? "Pay by Link"
                        : paymentMethod}
                    </p>
                  </div>
                )}

                {/* Method Mode */}
                <div className="bg-cblack4/60 p-3 rounded-lg border border-gray-800/80">
                  <span className="text-[11px] text-cwhite3 font-medium">Method Mode</span>
                  <p className="text-xs font-semibold text-white capitalize mt-1">
                    {depositInfo?.method || (isPaid ? "Manual" : "N/A")}
                  </p>
                </div>

                {/* Provider */}
                {depositInfo?.provider && (
                  <div className="bg-cblack4/60 p-3 rounded-lg border border-gray-800/80">
                    <span className="text-[11px] text-cwhite3 font-medium">Payment Provider</span>
                    <p className="text-xs font-semibold text-white capitalize mt-1">
                      {depositInfo.provider}
                    </p>
                  </div>
                )}

                {/* Terminal Flag */}
                {depositInfo?.via_terminal && (
                  <div className="bg-cblack4/60 p-3 rounded-lg border border-gray-800/80">
                    <span className="text-[11px] text-cwhite3 font-medium">Device Terminal</span>
                    <p className="text-xs font-semibold text-white mt-1 flex items-center gap-1">
                      <span className="text-cgreen flex items-center gap-1">
                        <Check className="h-3.5 w-3.5" />
                        Terminal #{singleReservation?.table_id || "Active"}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Alert notice if unpaid */}
              {!isPaid && !isRefunded && Number(singleReservation?.advance) > 0 && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg border border-cyellow/30 bg-cyellow/5 text-xs text-cwhite2">
                  <Info className="h-4 w-4 text-cyellow shrink-0 mt-0.5" />
                  <p>
                    An advance deposit of <strong>£{singleReservation?.advance}</strong> is required for this reservation. Use the action buttons below to collect payment.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Second Row: Reservation Schedule & Seating */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Reservation Schedule Details */}
          <Card className="bg-cblack3 border-gray-800 text-white lg:col-span-2 shadow-sm">
            <div className="p-4 border-b border-gray-800/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-cblue" />
                <h3 className="font-semibold text-white text-base">Booking & Schedule Breakdown</h3>
              </div>
            </div>
            <CardContent className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <span className="text-xs text-cwhite3">Reservation Date</span>
                  <p className="text-sm font-semibold text-white">
                    {formatDate(singleReservation?.reservation_date)}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-cwhite3">Issue / Booked Date</span>
                  <p className="text-sm font-semibold text-white">
                    {formatDate(singleReservation?.issue_date)}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-cwhite3">Reservation Type</span>
                  <p className="text-sm font-semibold text-white capitalize">
                    {singleReservation?.reservation_type === "phone"
                      ? "Phone Order"
                      : singleReservation?.reservation_type === "in_person"
                      ? "In Person"
                      : singleReservation?.reservation_type === "online"
                      ? "Online Booking"
                      : "Standard"}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-cwhite3">Start Time</span>
                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-cyellow" />
                    {formatTo12HourTime(singleReservation?.start_time)}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-cwhite3">End Time</span>
                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-cpurple" />
                    {formatTo12HourTime(singleReservation?.end_time)}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-xs text-cwhite3">Number of Guests</span>
                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-sky-400" />
                    {singleReservation?.guest || 1} Person(s)
                  </p>
                </div>
              </div>

              {/* Special Note Box if present */}
              {singleReservation?.note && (
                <div className="mt-5 p-3.5 rounded-lg bg-cblack4 border border-gray-800">
                  <div className="flex items-center gap-2 text-xs font-semibold text-cyellow">
                    <FileText className="h-3.5 w-3.5" />
                    Special Instructions / Notes
                  </div>
                  <p className="text-xs text-cwhite2 mt-1.5 leading-relaxed">
                    {singleReservation.note}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff & Operational Info */}
          <Card className="bg-cblack3 border-gray-800 text-white shadow-sm flex flex-col justify-between">
            <div className="p-4 border-b border-gray-800/80 flex items-center gap-2">
              <User className="h-4 w-4 text-cgreen" />
              <h3 className="font-semibold text-white text-base">Service & Staff</h3>
            </div>
            <CardContent className="p-5 space-y-4 flex-1">
              <div className="space-y-2">
                <span className="text-xs text-cwhite3">Assigned Staff</span>
                <div className="flex items-center gap-3 bg-cblack4 p-3 rounded-lg border border-gray-800">
                  <div className="h-8 w-8 rounded-full bg-cgreen/10 text-cgreen flex items-center justify-center font-bold text-xs">
                    {singleReservation?.staff?.name?.charAt(0) || "S"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {singleReservation?.staff?.name || "Unassigned"}
                    </p>
                    <p className="text-[11px] text-cwhite3">Service Representative</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-xs text-cwhite3">Linked POS Order</span>
                <div className="bg-cblack4 p-3 rounded-lg border border-gray-800 flex items-center justify-between">
                  <span className="text-xs text-cwhite2">
                    {singleReservation?.order_id ? `Order #${singleReservation.order_id}` : "No Order Linked"}
                  </span>
                  {singleReservation?.order_id && (
                    <span className="text-[10px] bg-cgreen/20 text-cgreen px-2 py-0.5 rounded font-bold">
                      Linked
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Seating & Table Allocation Section */}
        <Card className="bg-cblack3 border-gray-800 text-white shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-800/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Utensils className="h-4 w-4 text-cyellow" />
              <h3 className="font-semibold text-white text-base">Seating & Table Allocation</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2.5 py-1 rounded-full bg-cblack4 border border-gray-800 text-cwhite2 font-medium">
                {singleReservation?.tables?.length || 0} Table(s) Allocated
              </span>
              {singleReservation?.tables?.length > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-cyellow/15 border border-cyellow/30 text-cyellow font-semibold">
                  {singleReservation.tables.reduce((acc, t) => acc + Number(t.seat || 0), 0)} Total Seats
                </span>
              )}
            </div>
          </div>

          <CardContent className="p-5">
            {singleReservation?.tables?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {singleReservation.tables.map((table, index) => (
                  <div
                    key={index}
                    className="bg-cblack4 rounded-xl border border-gray-800/90 hover:border-cyellow/40 transition-all shadow-sm overflow-hidden flex flex-col justify-between group"
                  >
                    {/* Card Header with Table Avatar */}
                    <div className="p-4 flex items-center justify-between border-b border-gray-800/70 bg-gradient-to-b from-cblack3/50 to-transparent">
                      <div className="flex items-center gap-3">
                        <div
                          style={{
                            backgroundColor: table?.table_size?.style?.backgroundColor || "#f59e0b20",
                            borderRadius: table?.table_size?.style?.borderRadius || "8px",
                          }}
                          className="h-11 w-11 flex items-center justify-center font-extrabold text-white text-sm shadow-md border border-white/15 shrink-0 bg-opacity-90"
                        >
                          {table.name}
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-sm tracking-wide">
                            Table {table.name}
                          </h4>
                          <span className="text-[11px] text-cwhite3 capitalize">
                            {table.shape || "Square"} Table
                          </span>
                        </div>
                      </div>

                      <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-cyellow/15 text-cyellow border border-cyellow/30 flex items-center gap-1 shrink-0">
                        <Users className="h-3 w-3" />
                        {table.seat || 0} Seats
                      </span>
                    </div>

                    {/* Table Meta Details */}
                    <div className="p-3.5 space-y-2 text-xs">
                      <div className="flex justify-between items-center bg-cblack3/60 px-2.5 py-1.5 rounded border border-gray-800/50">
                        <span className="text-cwhite3 flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-cblue" />
                          Floor Location
                        </span>
                        <span className="text-white font-medium">
                          {table.floor?.name || `Floor #${table.floor_id || "1"}`}
                        </span>
                      </div>

                      <div className="flex justify-between items-center bg-cblack3/60 px-2.5 py-1.5 rounded border border-gray-800/50">
                        <span className="text-cwhite3 flex items-center gap-1.5">
                          <LayoutGrid className="h-3.5 w-3.5 text-cpurple" />
                          Room Zone
                        </span>
                        <span className="text-white font-medium">
                          {table.room?.name || `Room #${table.room_id || "1"}`}
                        </span>
                      </div>

                      <div className="flex justify-between items-center pt-1 text-[11px]">
                        <span className="text-cgreen flex items-center gap-1.5 font-medium">
                          <span className="h-2 w-2 rounded-full bg-cgreen animate-pulse" />
                          Allocated & Ready
                        </span>
                        <span className="text-cwhite3 font-mono">
                          ID: #{table.id}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-8 bg-cblack4 rounded-xl border border-dashed border-gray-800 text-center">
                <div className="h-12 w-12 rounded-full bg-cblack7 flex items-center justify-center text-cwhite3 mb-2">
                  <Utensils className="h-5 w-5" />
                </div>
                <h4 className="font-semibold text-white text-sm">No Table Allocated Yet</h4>
                <p className="text-xs text-cwhite3 max-w-sm mt-1">
                  This reservation does not have assigned tables. Click <strong>Assign Table / Room</strong> below to allocate seats.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Controls Toolbar */}
        <div className="flex items-center gap-3 flex-wrap p-4 bg-cblack3 rounded-xl border border-gray-800 mt-2 shadow-md">
          {/* Assign Table */}
          {hasPermission("reservation.reservation_list.assign_table") && (
            <Button
              className="bg-sky-500 hover:bg-sky-600 text-cwhite disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isDisabled || singleReservation?.tables?.length > 0}
              onClick={() => {
                setIsAssignTableModalOpen(true);
              }}
            >
              <Utensils className="w-4 h-4" />
              Assign Table / Room
            </Button>
          )}

          {/* Pay Over Phone / Cash */}
          {hasPermission("reservation.reservation_list.pay_by_phone") && (
            <Button
              className="bg-cviolet hover:bg-cviolet/90 text-cwhite disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isDepositPaid || isOnline || isDisabled || !Number(singleReservation?.advance)}
              onClick={() => setPayByPhoneDialog(true)}
            >
              <Banknote className="w-4 h-4" />
              {isDepositPaid ? "Deposit Paid" : "Cash / Pay Over Phone"}
            </Button>
          )}

          {/* Card Payment */}
          {(hasPermission("reservation.reservation_list.pay_by_card") || hasPermission("reservation.reservation_list.pay_by_phone")) && (
            <Button
              className="bg-cgreen hover:bg-cgreen/90 text-cwhite disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isDepositPaid || isOnline || isDisabled || !Number(singleReservation?.advance)}
              isLoading={cardPaymentLoading}
              onClick={cardPaymentHandler}
            >
              <CreditCard className="h-4 w-4" />
              Card Payment
            </Button>
          )}

          {/* Pay by Link */}
          {hasPermission("reservation.reservation_list.pay_by_link") && (
            <Button
              disabled={isDepositPaid || isOnline || isDisabled || !settings?.has_payment_provider || !Number(singleReservation?.advance)}
              className="bg-cblue hover:bg-cblue/90 text-cwhite disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              onClick={() => setPayByLinkDialog(true)}
            >
              <Link2 className="w-4 h-4" />
              Pay by link
            </Button>
          )}

          {/* Cancel Active Pay by Link (only shown when pay by link has been sent / active) */}
          {hasPermission("reservation.reservation_list.pay_by_link") && isLinkActive && (
            <Button
              variant="outline"
              className="border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200 flex items-center gap-2 animate-fadeIn"
              onClick={() => setCancelDepositLinkDialog(true)}
              title="Cancel Active Deposit Payment Link"
            >
              <Link2Off className="w-4 h-4 text-amber-400" />
              Cancel Payment Link
            </Button>
          )}

          {/* Cancel Reservation */}
          {hasPermission("reservation.reservation_list.cancel_reservation") && (
            <Button
              onClick={handleCancelClick}
              disabled={isDisabled}
              className="bg-cred hover:bg-cred/90 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto"
            >
              <XCircle className="w-4 h-4" />
              Cancel Reservation
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <CancelDialog
        isOpen={cancelDialog}
        onClose={() => setCancelDialog(false)}
        onConfirm={cancelHandler}
        isLoading={cancelLoading}
        title="Cancel Reservation?"
        description={
          isPaid
            ? `Cancelling this reservation will release allocated tables and dispatch a deposit refund of £${singleReservation?.advance || "0"} to the customer.`
            : "Are you sure you want to cancel this reservation? Any allocated tables will be released."
        }
        reservation={singleReservation}
        confirmText="Yes, Cancel"
        cancelText="Keep Reservation"
      />

      <SendPaymentLinkDialog
        isOpen={payByLinkDialog}
        onClose={() => setPayByLinkDialog(false)}
        onSend={payByLinkHandler}
        isLoading={payByLinkLoading}
        orderLabel={`Reservation #${singleReservation?.id || ""} - £${singleReservation?.advance || "0"}`}
        email={singleReservation?.customer?.email}
        phone={singleReservation?.customer?.number}
      />
      {/* Pending Conflict Dialog */}
      <PendingPayLinkConflictDialog
        isOpen={isConflictDialogOpen}
        onClose={() => {
          setIsConflictDialogOpen(false);
          setConflictData(null);
        }}
        title="Active Payment Link Already Exists"
        orderLabel={`Reservation #${singleReservation?.unique_id || singleReservation?.id || ""} - £${parseFloat(singleReservation?.advance || 0).toFixed(2)}`}
        existingPayLink={conflictData}
        targetChannel={conflictTargetChannel}
        onCancelAndResend={handleConflictCancelAndResend}
        onCancelOnly={handleConflictCancelOnly}
        isLoading={isConflictLoading}
      />

      {/* Cancel Active Pay by Link dialog */}
      <CancelDepositPaymentLinkDialog
        isOpen={cancelDepositLinkDialog}
        onClose={() => setCancelDepositLinkDialog(false)}
        onConfirm={cancelDepositPaymentLinkHandler}
        isLoading={cancelDepositLinkLoading}
        reservation={singleReservation}
      />

      {/* Pay by over Phone confirmation */}
      <CancelDialog
        isOpen={payByPhoneDialog}
        onClose={() => setPayByPhoneDialog(false)}
        onConfirm={payByPhoneHandler}
        isLoading={payByPhoneLoading}
        title="Mark Deposit as Paid?"
        description={`Confirm receipt of £${singleReservation?.advance || "0"} cash or phone deposit for reservation ${singleReservation?.unique_id || `#${singleReservation?.id}`}.`}
        icon={<Banknote className="h-6 w-6 text-cgreen" />}
        confirmText="Mark as Paid"
        cancelText="Cancel"
      />

      {isAssignTableModalOpen && (
        <AssignTableModal
          isOpen={isAssignTableModalOpen}
          onClose={closeAssignTableModal}
          singleReservation={singleReservation}
        />
      )}

      <TerminalPaymentResponseDialog
        isOpen={cardPaymentDialogOpen}
        onClose={() => {
          setCardPaymentDialogOpen(false);
          setCardPaymentResponse(null);
        }}
        responseData={cardPaymentResponse}
        reservation={singleReservation}
        onPaymentCompleted={() => {
          setCardPaymentResponse(null);
          dispatch(fetchReservationById(id));
        }}
        onPaymentCancelled={() => {
          setCardPaymentResponse(null);
          setIsCancelledEvent(false);
          dispatch(fetchReservationById(id));
        }}
      />

      <ReservationRefundResponseDialog
        isOpen={refundResponseDialogOpen}
        onClose={() => {
          setRefundResponseDialogOpen(false);
          setRefundResponseData(null);
        }}
        responseData={refundResponseData}
        reservation={singleReservation}
        onRefundCompleted={() => {
          setRefundResponseData(null);
          dispatch(fetchReservationById(id));
        }}
        onRefundCancelled={() => {
          setRefundResponseData(null);
          dispatch(fetchReservationById(id));
        }}
      />

      <PaxPaymentTimeoutDialog
        isOpen={isPaxTimeoutDialogOpen}
        onClose={() => setIsPaxTimeoutDialogOpen(false)}
        onNewPayment={() => {
          setIsPaxTimeoutDialogOpen(false);
          if (isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund")) {
            cancelHandler();
          } else {
            cardPaymentHandler();
          }
        }}
        orderData={{
          id: singleReservation?.id,
          advance: singleReservation?.advance,
          total_price: singleReservation?.advance,
          reference: paxTimeoutData?.unique_id || singleReservation?.unique_id || `RES-${singleReservation?.id || singleReservation?.booking_id || ""}`,
          unique_id: paxTimeoutData?.unique_id || singleReservation?.unique_id,
          isRefund: isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund"),
        }}
        timeoutData={paxTimeoutData}
        title={
          isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund")
            ? "PAX Refund Timed Out"
            : "PAX Deposit Payment Timed Out"
        }
        description={
          isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund")
            ? "The refund request on the PAX terminal has timed out. Click 'Cancel Again' to dispatch a fresh refund request and cancel the reservation."
            : "The payment request on the PAX terminal has timed out. You need to dispatch a new payment request to proceed."
        }
        amountLabel={
          isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund")
            ? "Refund Amount"
            : "Deposit Amount"
        }
        buttonText={
          isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund")
            ? "Cancel Again"
            : "New Payment"
        }
        isLoading={
          isRefundAction || paxTimeoutData?.event_type?.includes("refund") || paxTimeoutData?.type?.includes("refund")
            ? cancelLoading
            : cardPaymentLoading
        }
      />
    </Layout2>
  );
}

export default ReservationDetails;
